import { createContext, useEffect, useCallback, useContext, useMemo } from 'react'
import { decodeGlobalId } from '~/utils/id'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import { getCookie, setCookie } from 'cookies-next'

// The following function content is copy pasted from
// https://developers.intercom.com/installing-intercom/docs/basic-javascript
// We keep it like this so we can easily copy paste an update if needed.
const initializeIntercom = appId => {
  const intercom = window.Intercom
  if (typeof intercom === 'function') {
    intercom('reattach_activator')
    intercom('update', window.intercomSettings)
  } else {
    const instance = function () {
      instance.c(arguments)
    }
    instance.q = []
    instance.c = function (args) {
      instance.q.push(args)
    }
    window.Intercom = instance
    const load = function () {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = `https://widget.intercom.io/widget/${appId}`
      const element = document.getElementsByTagName('script')[0]
      if (element) {
        element.parentNode.insertBefore(script, element)
      }
    }
    if (document.readyState === 'complete') {
      load()
    } else if (window.attachEvent) {
      window.attachEvent('onload', load)
    } else {
      window.addEventListener('load', load, false)
    }
  }
}

const IntercomContext = createContext()
export const IntercomConsumer = IntercomContext.Consumer

export const useIntercom = () => {
  const context = useContext(IntercomContext)

  if (!context) {
    throw new Error('useIntercom() must be used within a IntercomProvider')
  }

  return context
}

export const IntercomProvider = ({ children, appId, initialState }) => {
  const { loggedInUser } = useLoggedInUser()
  const isLoggedIn = Boolean(loggedInUser)

  const { id, firstname: name, email, intercomVerificationId: user_hash } = loggedInUser || {}
  const options = useMemo(() => {
    const engaged = getCookie('Intercom-engaged') === '1' || Boolean(initialState)

    if (!isLoggedIn) {
      return {
        hide_default_launcher: !engaged,
      }
    }

    return {
      user_id: decodeGlobalId(id),
      name,
      email,
      user_hash,
      hide_default_launcher: !engaged,
    }
  }, [isLoggedIn, id, name, email, user_hash, initialState])

  const initialize = useCallback(() => {
    if (!window.Intercom) {
      initializeIntercom(appId)
    }
  }, [appId])

  const boot = useCallback(() => {
    window.Intercom('boot', {
      app_id: appId,
      ...options,
    })
  }, [appId, options])

  const update = useCallback(() => {
    window.Intercom('update', {
      ...options,
    })
  }, [options])

  const bootOrUpdate = useCallback(() => {
    if (!window.Intercom.booted) {
      boot()
    } else {
      update()
    }
  }, [boot, update])

  const setupEventHandlers = useCallback(() => {
    window.Intercom('onHide', () => setCookie('Intercom-show', '0'))
    window.Intercom('onShow', () => setCookie('Intercom-show', '1'))
  }, [])

  const open = useCallback(() => {
    // Update engaged cookie for default value in state
    setCookie('Intercom-engaged', '1')
    window.Intercom('update', { hide_default_launcher: false })
    window.Intercom('show')
  }, [])

  const openWithMessage = useCallback((message = '') => {
    // Update engaged cookie for default value in state
    setCookie('Intercom-engaged', '1')
    window.Intercom('update', { hide_default_launcher: false })
    window.Intercom('showNewMessage', message)
  }, [])

  const openWithArticle = useCallback(articleId => {
    // Update engaged cookie for default value in state
    setCookie('Intercom-engaged', '1')
    window.Intercom('update', { hide_default_launcher: false })
    window.Intercom('showArticle', articleId)
  }, [])

  const reboot = useCallback(() => {
    // Remove current user session (for when user is on shared device)
    window.Intercom('shutdown')

    // Boot new session
    window.Intercom('boot', {
      app_id: appId,
      hide_default_launcher: true,
    })

    // Remove the intercom "bubble" at the bottom of the page
    setCookie('Intercom-engaged', '0')
  }, [appId])

  const trackEvent = useCallback((event, metadata) => {
    if (typeof window.Intercom === 'undefined') return

    window.Intercom('trackEvent', event, metadata)
  }, [])

  useEffect(() => {
    initialize()
    bootOrUpdate()
    setupEventHandlers()

    if (getCookie('Intercom-show') === '1' || initialState) {
      if (initialState !== 'show' && initialState !== 'showMessage') {
        openWithArticle(parseInt(initialState))
      } else if (initialState === 'showMessage') {
        openWithMessage()
      } else {
        open()
      }
    }
  }, [
    initialState,
    initialize,
    bootOrUpdate,
    setupEventHandlers,
    open,
    openWithMessage,
    openWithArticle,
  ])

  const show = () => {
    bootOrUpdate()
    open()
  }

  const showMessage = message => {
    bootOrUpdate()
    openWithMessage(message)
  }

  const showArticle = articleId => {
    bootOrUpdate()
    openWithArticle(articleId)
  }

  return (
    <IntercomContext.Provider value={{ show, showMessage, showArticle, reboot, trackEvent }}>
      {children}
    </IntercomContext.Provider>
  )
}
